import { useMemo } from 'react'

import {
  digObject, matchFilterNumber, matchFilterString,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useSelector from '@hooks/useSelector'

import type {
  OrderModel,
  OrderRequestOptions, StatusModel,
} from '@models/types'

import useReduxAction from '@hooks/useReduxAction'

import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['orders', 'statuses']

const CURRENT_STATUSES = ['approved', 'processing', 'printed', 'packaged', 'held', 'processed', 'urgent']

type GroupedOrder = { order: OrderModel, orderItemIds: number[] }

const sortOrders = (filteredOrders: GroupedOrder[]) => filteredOrders.sort((a, b) => {
  const dateA = digObject(a.order, 'dates.approval.integer')
  const dateB = digObject(b.order, 'dates.approval.integer')

  if (dateA > dateB) return -1
  if (dateA < dateB) return 1

  return 0
})

const matchOrderItemStatus = (orderItemStatus: Partial<StatusModel>, filterStatusId: number | string) => {
  const { id, key = '' } = orderItemStatus

  if (filterStatusId === 'current'){
    return CURRENT_STATUSES.includes(key)
  }

  // @ts-expect-error TS2345
  return matchFilterNumber(id, filterStatusId)
}

 type EntityOrdersFilters = {
  agency_id?: number,
  publication_id?: number,
  service_id?: number,
  status_id?: number,
  string?: string,
}

type UseEntityOrdersOptions = {
  filters?: EntityOrdersFilters,
  performHttpRequests?: boolean,
  requestOptions?: OrderRequestOptions,
}

function useOrdersList(options: UseEntityOrdersOptions) {
  const { filters = {}, requestOptions } = options

  const {
    agency_id: filterAgencyId,
    publication_id: filterPublicationId,
    service_id: filterServiceId,
    status_id: filterStatusId,
    string: filterString,
  } = filters || {}

  const {
    updatedEntities: { orders: ordersUpdatedAt, statuses: statusesUpdateAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const {
    campaigns, orders, orderItems, statuses,
  } = useSelector(reduxState => reduxState.entities)

  const filteredOrders = useMemo(() => {
    const filtered: { [key: number]: GroupedOrder } = {}

    Object.values(orderItems).forEach((orderItem) => {
      const orderItemStatus = statuses[orderItem.status_id] || {}
      const order = orders[orderItem.order_id] || {}
      const campaign = campaigns[order?.campaign_id] || {}

      const {
        agency_id, id: orderId, publication_id, shipping_address, title,
      } = order

      const { service_id } = orderItem

      const matchId = orderId?.toString() === filterString
      const matchCampaignAddress = matchFilterString(campaign?.address, filterString)
      const matchOrderAddress = matchFilterString(shipping_address, filterString)
      const matchOrderTitle = matchFilterString(title, filterString)

      const matchString = matchId || matchCampaignAddress || matchOrderAddress || matchOrderTitle

      const matchAgencyId = matchFilterNumber(agency_id, filterAgencyId)
      const matchPublicationId = matchFilterNumber(publication_id, filterPublicationId)
      const matchServiceId = matchFilterNumber(service_id, filterServiceId)

      const matchStatus = filterStatusId ? matchOrderItemStatus(orderItemStatus, filterStatusId) : true

      if (matchAgencyId && matchString && matchStatus && matchPublicationId && matchServiceId && !!orderId){
        if (!filtered[orderId]){
          filtered[orderId] = {
            order,
            orderItemIds: [],
          }
        }

        filtered[orderId]?.orderItemIds.push(orderItem.id)
      }
    })

    return sortOrders(Object.values(filtered))
  }, [ordersUpdatedAt, statusesUpdateAt, JSON.stringify(filters)])

  const filteredOrdersCount = Object.values(filteredOrders).length
  const hasFilteredOrders = !!filteredOrdersCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredOrdersCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingOrders } = useReduxAction(
    'orders',
    'loadOrders',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredOrders,
    filteredOrdersCount,
    hasFilteredOrders,
    loading: loadingOrders,
  }
}

export default useOrdersList
